import { OVERALL_SEVERITY_COLOR } from "@constants/resultColors";

export type OverallSeverity = "normal" | "monitor" | "attention";

export interface OverallStatus {
  severity: OverallSeverity;
  color: string;
  label: string;
}

const SEVERITY_RANK: Record<OverallSeverity, number> = {
  normal: 0,
  monitor: 1,
  attention: 2,
};

const DEFAULT_LABEL_BY_SEVERITY: Record<OverallSeverity, string> = {
  normal: "Overall: Normal",
  monitor: "Overall: Monitor",
  attention: "Overall: Attention Required",
};

const ATTENTION_TOKENS = new Set([
  "attention",
  "high",
  "abnormal",
  "elevated",
  "critical",
  "needs review",
  "needsreview",
  "caution",
]);

const MONITOR_TOKENS = new Set(["monitor", "moderate", "medium", "borderline", "near_orthophoria"]);

const NORMAL_TOKENS = new Set(["normal", "ok", "low", "low-risk", "good"]);

export function classifyStatus(status: string | null | undefined): OverallSeverity | null {
  if (!status) return null;
  const n = status.toLowerCase().trim();
  if (ATTENTION_TOKENS.has(n)) return "attention";
  if (MONITOR_TOKENS.has(n)) return "monitor";
  if (NORMAL_TOKENS.has(n)) return "normal";
  return null;
}

export function aggregateSeverity(
  statuses: Array<string | null | undefined>,
): OverallSeverity | null {
  let worst: OverallSeverity | null = null;
  for (const status of statuses) {
    const severity = classifyStatus(status);
    if (severity == null) continue;
    if (worst == null || SEVERITY_RANK[severity] > SEVERITY_RANK[worst]) {
      worst = severity;
    }
  }
  return worst;
}

export function buildOverallStatus(
  statuses: Array<string | null | undefined>,
  labels?: Partial<Record<OverallSeverity, string>>,
): OverallStatus | null {
  const severity = aggregateSeverity(statuses);
  if (severity == null) return null;
  return {
    severity,
    color: OVERALL_SEVERITY_COLOR[severity],
    label: labels?.[severity] ?? DEFAULT_LABEL_BY_SEVERITY[severity],
  };
}
